import { Button, Col, Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'react-bootstrap';
import PipelineMap from '../map/pipeline-map';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useWidgetLoaderData, LocalWidgetRouterContext } from '@/core/widgets/export';
import { Notice, FullNotice } from '../../../model/pipeline-alerts/pipeline-alert';
import { entityApi, pipelineNotificationApi } from '../../../api';
import useCopyLinkAction from '@/modules/pipelines/hooks/use-copy-link-action';
import {
  pipelineGraphToGeoJson,
  pipelineGraphToZonesGeoJson,
  ZoneFC,
} from '../../../data/pipelines';
import { PipelineNode } from '../../../api/entity-api';
import { queryOfWidgetPath, readMapUrlState } from '../../../data/map-url-state';
import { Page } from '@/shared/pagination/export';
import { MINIMAL_PIPELINE_HISTORY_SIZE } from '@/modules/pipelines/api/pipeline-notification-api';
import { EntityNode } from '@/modules/pipelines/model/pipeline-alerts/entity-node';
import { getEntitiesByIds, getOwnsAncestry } from '@/modules/pipelines/data/owns-ancestry';
import NoticeOwnershipBreadcrumb from './notice-ownership-breadcrumb';
import { TopicSubscribeButton } from '@/core/notifications/export';
import {
  RequirePermissions,
  Permissions,
  useEffectivePermissions,
} from '@/core/permissions/export';
import {
  PIPELINE_NOTICE_TOPIC_KEY,
  pipelineSubscriptionName,
  pipelineTagFilter,
} from '@/modules/pipelines/data/notice-subscription';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLink } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const flattenGeoJsonCoords = (coords: any): [number, number][] => {
  if (!Array.isArray(coords)) return [];

  if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
    return [coords as [number, number]];
  }

  return coords.flatMap(flattenGeoJsonCoords);
};

const calculateGeoCenter = (points: [number, number][]): [number, number] | null => {
  if (points.length === 0) return null;

  const total = points.reduce((acc, [lng, lat]) => [acc[0] + lng, acc[1] + lat], [0, 0]);
  return [total[0] / points.length, total[1] / points.length];
};

const calculateBBoxSWNE = (
  points: [number, number][],
  padding = 1,
): [[number, number], [number, number]] | null => {
  if (points.length === 0) return null;

  let minLng = points[0][0],
    maxLng = points[0][0];
  let minLat = points[0][1],
    maxLat = points[0][1];

  for (let i = 1; i < points.length; i++) {
    const [lng, lat] = points[i];
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }

  return [
    [minLng - padding, minLat - padding],
    [maxLng + padding, maxLat + padding],
  ];
};

export default function PipelineMapNoticePanel() {
  const localRouter = useContext(LocalWidgetRouterContext);
  const [initialUrlState] = useState(() =>
    readMapUrlState(new URLSearchParams(queryOfWidgetPath(localRouter?.path))),
  );
  // The minimap is part of the map feature, so it follows the same entitlement as the
  // Map tab. Read the token directly, as PipelineMapTabBar does — useHasPermission
  // would return true unconditionally in the test environment.
  const hasPlus = useEffectivePermissions().includes(Permissions.PETRO_PIPELINES_PLUS);

  const loaderData = useWidgetLoaderData() as {
    notice: FullNotice;
    history: Page<Notice> | undefined;
  };

  const [selectedNotice, setSelectedNotice] = useState<FullNotice>();
  const [selectedHistory, setSelectedHistory] = useState<Notice[]>();
  const [page, setPage] = useState(0);
  const [pipelines, setPipelines] = useState<PipelineNode[]>();
  const [zones, setZones] = useState<ZoneFC>();
  const [ownsChain, setOwnsChain] = useState<EntityNode[]>([]);
  const [affectedEntities, setAffectedEntities] = useState<EntityNode[]>([]);

  const [mapCenter, setMapCenter] = useState<[number, number]>([-98.5, 39.5]);
  const [mapZoom, setMapZoom] = useState<number>(1);
  const [centerGeometry, setCenterGeometry] = useState<any>();
  const [bBox, setBBox] = useState<[[number, number], [number, number]]>();
  const [notFound, setNotFound] = useState(false);

  const storedHistory = useRef<Map<number, Notice[]>>(new Map());
  const storedNotices = useRef<Map<string, FullNotice>>(new Map());
  const urlCameraPending = useRef(!!initialUrlState.camera);

  const now = useRef(dayjs(Date.now()));

  useCopyLinkAction();

  useEffect(() => {
    if (!loaderData) return;

    if (loaderData.notice) {
      setSelectedNotice(loaderData.notice);
      storedNotices.current.set(loaderData.notice._id, loaderData.notice);
    }
    if (loaderData.history) {
      setSelectedHistory(loaderData.history.content);
      storedHistory.current.set(page, loaderData.history.content);
    }
  }, [loaderData, page]);

  useEffect(() => {
    if (!selectedNotice || !selectedHistory) return;

    if (storedHistory.current.has(page)) setSelectedHistory(storedHistory.current.get(page));
    else {
      pipelineNotificationApi.getPipelineEventHistory(selectedNotice.groupId, page).then(r => {
        setSelectedHistory(r.content);
        storedHistory.current.set(page, r.content);
      });
    }
    // eslint-disable-next-line
  }, [selectedNotice, page]);

  useEffect(() => {
    const related = selectedNotice?.relatedEntities;
    if (!related?.length) {
      setOwnsChain([]);
      setAffectedEntities([]);
      return;
    }

    let isCancelled = false;
    Promise.all([getOwnsAncestry(related[0]), getEntitiesByIds(related)])
      .then(([chain, affected]) => {
        if (isCancelled) return;
        setOwnsChain(chain);
        setAffectedEntities(affected);
      })
      .catch(() => {
        if (isCancelled) return;
        setOwnsChain([]);
        setAffectedEntities([]);
      });

    return () => {
      isCancelled = true;
    };
  }, [selectedNotice]);

  useEffect(() => {
    if (!selectedNotice) return;

    entityApi.getPipelineGraph().then(res => {
      const pipeline = res.find(r => r.pipeline === selectedNotice.operator._id);
      setPipelines(pipeline ? [pipeline] : []);
      setCenterGeometry(pipeline?.segments.map(seg => seg.geometry?.coordinates));
    });
  }, [selectedNotice]);

  useEffect(() => {
    if (!pipelines) return;
    setZones(pipelineGraphToZonesGeoJson(pipelines));
  }, [pipelines]);

  useEffect(() => {
    if (!selectedNotice || !centerGeometry) return;
    if (urlCameraPending.current) {
      urlCameraPending.current = false;
      return;
    }

    const flatCoords = flattenGeoJsonCoords(centerGeometry);
    const center = calculateGeoCenter(flatCoords);
    const bbox = calculateBBoxSWNE(flatCoords);

    if (center && bbox) {
      setMapCenter(center);
      setMapZoom(4);
      setBBox(bbox);
    } else {
      setMapCenter([-98.5, 39.5]);
      setMapZoom(1);
      setBBox(undefined);
      setNotFound(true);
    }
  }, [selectedNotice, centerGeometry]);

  const pipelineFilter = useMemo(
    () => pipelineTagFilter(selectedNotice?.operator),
    [selectedNotice],
  );

  const subscribeAction = pipelineFilter ? (
    <RequirePermissions allow={Permissions.NOTIFICATIONS}>
      <span className="flex-shrink-0">
        <TopicSubscribeButton
          topicKey={PIPELINE_NOTICE_TOPIC_KEY}
          tagFilter={pipelineFilter}
          suggestedName={pipelineSubscriptionName(selectedNotice?.operator)}
          showLabel
        />
      </span>
    </RequirePermissions>
  ) : null;

  const OwnerDisplay = () => {
    if (!selectedNotice?.operator) return null;
    const { owner, name } = selectedNotice?.operator;
    const displayName = new RegExp(`^${name}`, 'i').test(owner) ? name : `${owner} / ${name}`;
    return ownsChain.length > 0 ? (
      <NoticeOwnershipBreadcrumb chain={ownsChain} affected={affectedEntities} />
    ) : (
      <span className="fw-semibold" style={{ wordBreak: 'break-all' }}>
        {displayName}
      </span>
    );
  };

  // const [big, setBig] = useState(false); // for testing

  return (
    <div className="d-flex flex-column h-100 overflow-hidden">
      <div className="d-flex align-items-center gap-2 pb-2 border-bottom">
        <h3 className="mb-0">{selectedNotice?.eventType}</h3>
        <Button
          size="sm"
          variant="link"
          href={selectedNotice?.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          <FontAwesomeIcon icon={faExternalLink} size="sm" className="py-0" />
        </Button>
        {subscribeAction}
      </div>

      <div className={`d-flex flex-grid flex-shrink-0 justify-content-between border-bottom`}>
        <Col
          className="d-flex flex-column gap-1 justify-content-between py-2"
          md={hasPlus ? 8 : 12}
          // md={big ? 5 : 8}
          style={{
            transition: 'flex 0.4s ease-in-out, max-width 0.4s ease-in-out, width 0.4s ease-in-out',
          }}
        >
          <div className="d-flex flex-column gap-1">
            <OwnerDisplay />

            {selectedHistory ? (
              <Dropdown>
                <DropdownToggle variant="outline-secondary" className="py-0" id="history-dropdown">
                  {dayjs(selectedNotice?.postedAt).format('MMM D, YYYY h:mm:ss A')}
                </DropdownToggle>

                <DropdownMenu className="border-secondary px-1" style={{ boxSizing: 'border-box' }}>
                  {selectedHistory.map(h => (
                    <DropdownItem
                      key={h._id}
                      className="px-1 rounded-3"
                      active={h._id === selectedNotice?._id}
                      onClick={() => {
                        if (storedNotices.current.has(h._id)) {
                          setSelectedNotice(storedNotices.current.get(h._id));
                        } else {
                          pipelineNotificationApi.getFullPipelineAlert(h._id).then(r => {
                            setSelectedNotice(r);
                            storedNotices.current.set(h._id, r);
                          });
                        }
                      }}
                    >
                      {(() => {
                        const date = dayjs(h.postedAt);
                        let displayText = '';

                        if (date.isSame(now.current, 'day')) {
                          displayText = `Today, ${date.format('h:mm A')}`;
                        } else if (date.isAfter(now.current.subtract(2, 'days'))) {
                          displayText = `${date.fromNow()}, ${date.format('h:mm A')}`;
                        } else {
                          displayText = date.format('MMM D, YYYY hh:mm:ss A');
                        }

                        return displayText;
                      })()}
                    </DropdownItem>
                  ))}

                  <div className="d-flex mb-1 justify-content-between align-items-center">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={page === 0}
                      onClick={() => {
                        setPage(prev => prev - 1);
                      }}
                    >
                      {'<'}
                    </Button>
                    <span>{page + 1}</span>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={selectedHistory.length < MINIMAL_PIPELINE_HISTORY_SIZE}
                      onClick={() => {
                        setPage(prev => prev + 1);
                      }}
                    >
                      {'>'}
                    </Button>
                  </div>
                </DropdownMenu>
              </Dropdown>
            ) : (
              <span className="flex-shrink-0">
                {dayjs(selectedNotice?.postedAt).format('MMM D, YYYY h:mm:ss A')}
              </span>
            )}
          </div>

          <div className="d-flex flex-column gap-1">
            <div className="d-flex flex-column">
              <span className="fw-semibold">Starting</span>
              <span>{dayjs(selectedNotice?.effectiveStart).format('MMM D, YYYY h:mm:ss A')}</span>
            </div>
            <div className="d-flex flex-column">
              <span className="fw-semibold">Ending</span>
              <span>{dayjs(selectedNotice?.effectiveEnd).format('MMM D, YYYY h:mm:ss A')}</span>
            </div>
          </div>
        </Col>

        {hasPlus && (
          <Col
            md={4}
            // md={big ? 7 : 4}
            style={{
              transition:
                'flex 0.4s ease-in-out, max-width 0.4s ease-in-out, width 0.4s ease-in-out',
            }}
          >
            <span
              style={{
                display: 'block',
                aspectRatio: '5 / 4',
                position: 'relative',
              }}
            >
              <PipelineMap
                pipelines={pipelineGraphToGeoJson(pipelines || [])}
                regions={undefined}
                zones={zones}
                initialCenter={mapCenter}
                initialZoom={mapZoom}
                initialBBox={bBox}
                notFoundOverlay={notFound}
              />

              {/* <Button style={{
                  position: 'absolute',
                  bottom: '10px',
                  right: '10px',
                  zIndex: 15,

                  width: '29px',
                  height: '29px',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',

                  backgroundColor: '#FBFBFE',
                  border: 'none',
                  borderRadius: '4px',
                  boxShadow: '0 0 0 2px rgba(0, 0, 0, 0.1)',
                  cursor: 'pointer'
                }}
                onClick={() => setBig(prev => !prev)}
              >
                <FontAwesomeIcon icon={faExpand} className='p-0' style={{color: '#333', fontSize: '15px'}}/>
              </Button> */}
            </span>
          </Col>
        )}
      </div>

      <div
        className={`petro-notes pt-2 flex-grow-1 overflow-auto`}
        style={{ maxHeight: '52vh', minHeight: '20vh' }}
        dangerouslySetInnerHTML={{ __html: selectedNotice?.operationNotes || '' }}
      ></div>
    </div>
  );
}
